<template>
    <v-container class="permission-modification">
        <v-expansion-panels
            v-model="panels"
            flat
        >
            <v-expansion-panel>
                <v-expansion-panel-header>
                    <h2>Permissions</h2>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                    <div class="permission-modification__input-group">
                        <div class="permission-modification__input-group--main">
                            <ClInput
                                id="email-input"
                                v-model="models.email"
                                :aria-label="( hasUserPermissionError ? userPermissionInfo.errorMessage : 'Edit Permissions by email address' )"
                                :class="{ 'is-warning': hasUserPermissionError }"
                                :title="( hasUserPermissionError ? userPermissionInfo.errorMessage : 'Edit Permissions by email address' )"
                                :disabled="inFlight || userPermissionInfo.inFlight"
                                :loading="inFlight || userPermissionInfo.inFlight"
                                placeholder="Edit Permissions by email address"
                                @keydown.enter="viewPermissionsHandler()"
                            />
                            <div class="permission-modification__input-group__buttons">
                                <ClButton
                                    class="button__clear"
                                    type="secondary"
                                    :disabled="userPermissionInfo.inFlight"
                                    @click="clearEmailHandler()"
                                >
                                    Clear
                                </ClButton>
                                <ClButton
                                    class="button__current-user"
                                    :disabled="userPermissionInfo.inFlight"
                                    @click="fillEmailHandler()"
                                >
                                    Current User
                                </ClButton>
                            </div>
                        </div>
                        <ClButton
                            class="button__view-permissions"
                            :disabled="userPermissionInfo.inFlight"
                            :loading="userPermissionInfo.inFlight"
                            @click="viewPermissionsHandler()"
                        >
                            View Permissions
                        </ClButton>
                    </div>

                    <!-- Permissions -->
                    <TableCurrentPermissions
                        v-if="canViewPermissions"
                        :userEmailAddress="models.email"
                        :locationList="locationInfo.list"
                        :currentPermissionsList="userPermissionInfo.list"
                        @addRole="( payload ) => addRoleHandler( payload )"
                        @removeRoles="( payload ) => removeRoleHandler( payload )"
                    />

                    <ModalModifyPermission
                        v-model="modifyModal.show"
                        :modalType="modifyModal.type"
                        :targetLocation="modifyModal.location"
                        :locationList="locationInfo.list"
                        :currentPermissionsList="userPermissionInfo.list"
                        @assignRoles="( payload ) => assignRolesHandler( payload )"
                        @close="clearModifyModal()"
                    />
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>
    </v-container>
</template>

<script>
// Vuex
import { mapGetters, mapState } from 'vuex';

// Api
import PermissionApi from '@/api/PermissionApi';

// Utils
import DataUtil from '@/utils/DataUtil';
import ErrorUtil from '@/utils/ErrorUtil';
import ValidationUtil from '@/utils/ValidationUtil';

// Components
import TableCurrentPermissions from './permissionModification/TableCurrentPermissions';
import ModalModifyPermission from './permissionModification/ModalModifyPermission';

export default {
    name: 'PermissionModification',
    components: {
        TableCurrentPermissions,
        ModalModifyPermission,
    },
    data() {
        return {
            panels: [ 0 ],

            hasSearched: false,
            models: {
                email: '',
            },

            modifyModal: {
                show: false,
                type: '', // Can be '', 'NewLocation', 'ExistingLocation'
                location: {},
            },

            locationInfo: {
                /** @type { Array } of Location { Object } */
                list: [],
                inFlight: false,
            },
            userPermissionInfo: {
                /** @type { Object } - A normalized list of Permissions keyed by their locationId */
                list: {},
                inFlight: false,
                errorMessage: '',
                isValidUser: false,
            },
        };
    },
    computed: {
        ...mapState( {
            jwtToken: ( state ) => state.User.jwtToken,
            userAccountId: ( state ) => state.User.accountId,
            username: ( state ) => state.User.username,
            password: ( state ) => state.User.password,
            currentUserPermissionsList: ( state ) => state.User.permissions.list,
        } ),
        ...mapGetters( {
            websiteId: 'Website/id',
        } ),

        /** @returns { Boolean } */
        inFlight() {
            return this.locationInfo.inFlight;
        },

        /** @returns { Boolean } */
        hasLocations() {
            return this.locationInfo.list.length > 0;
        },
        /** @returns { Boolean } */
        hasUserPermissions() {
            return Object.values( this.userPermissionInfo.list ).length > 0;
        },
        /** @returns { Boolean } */
        hasUserPermissionError() {
            return !!this.userPermissionInfo.errorMessage;
        },
        /** @returns { Boolean } */
        hasEmailQuery() {
            return this.models.email !== '';
        },

        /** @returns { Boolean } */
        canViewPermissions() {
            return this.hasSearched
                && this.hasEmailQuery
                && this.userPermissionInfo.isValidUser;
        },
        /** @returns { Boolean } */
        userHasNoPermissions() {
            return this.hasSearched
                && !this.hasUserPermissions;
        },
    },
    watch: {
        panels() {
            // Only get location list the first time they open the permissions expanded section
            if ( !this.hasLocations && !this.locationInfo.inFlight ) {
                this.getLocationList();
            }
        },
        // eslint-disable-next-line object-shorthand
        'models.email'() {
            this.hasSearched = false;
        },
    },
    methods: {
        setModifyModal( { show, type, location } ) {
            this.modifyModal = { show, type, location };
        },
        clearModifyModal() {
            this.setModifyModal( {
                show: false,
                type: '',
                location: {},
            } );
        },

        fillEmailHandler() {
            this.models.email = this.username;
            this.viewPermissionsHandler();
        },
        clearEmailHandler() {
            this.models.email = '';
            this.userPermissionInfo.errorMessage = '';
            this.hasSearched = false;
        },

        async getLocationList() {
            this.locationInfo.inFlight = true;
            try {
                const { data } = await PermissionApi.getLocationList( {
                    username: this.username,
                    password: this.password,
                } );
                this.locationInfo.list = ValidationUtil.arrayHasItems( data.locations )
                    ? data.locations
                    : [];
            } catch ( error ) {
                console.error( 'Error getting location list', error );
            } finally {
                this.locationInfo.inFlight = false;
            }
        },

        async viewPermissionsHandler() {
            if ( this.hasEmailQuery ) {
                await this.getPermissions();
                this.hasSearched = true;
            }
        },
        async getPermissions() {
            this.userPermissionInfo.errorMessage = '';
            this.userPermissionInfo.isValidUser = false;

            if ( this.models.email === this.username ) {
                this.userPermissionInfo.list = this.currentUserPermissionsList;
                this.userPermissionInfo.isValidUser = true;
                return;
            }

            this.userPermissionInfo.inFlight = true;
            try {
                const { data } = await PermissionApi.getUserMailPermissions( {
                    jwtToken: this.jwtToken,
                    userAccountId: this.userAccountId,
                    username: this.models.email,
                } );
                this.userPermissionInfo.list = ValidationUtil.arrayHasItems( data.locationPermissionsList )
                    ? DataUtil.normalize( data.locationPermissionsList, 'locationId' )
                    : {};
                this.userPermissionInfo.isValidUser = ( !!data.username && !!data.userAccountId );
                if ( !data.username || !data.userAccountId ) throw new Error( `No user was found with the email address: ${ this.models.email }` );
            } catch ( error ) {
                await this.$store.dispatch( 'snackbar', {
                    message: ErrorUtil.getMessageFromError( error ),
                    type: 'error',
                    duration: 5000,
                } );
                this.userPermissionInfo.errorMessage = ( error.response && error.response.data && error.response.data.message )
                    ? error.response.data.message
                    : error.message;
            } finally {
                this.userPermissionInfo.inFlight = false;
            }
        },

        async refreshPermissionsList() {
            if ( this.models.email === this.username ) {
                await this.$store.dispatch( 'User/getPermissions', { username: this.username } );
            }
            await this.getPermissions();
        },

        /**
         * @param { Number } locationId
         * @param { String[] } roles
         * @param { String } mode - 'add' or 'remove'
         */
        async modifyRolesForLocation( { locationId, roles, mode } ) {
            try {
                await PermissionApi.modifyUserMailPermissions( {
                    jwtToken: this.jwtToken,
                    websiteId: this.websiteId,
                    userAccountId: this.userAccountId,
                    modifiedPermissions: {
                        username: this.models.email,
                        mailPermissionsList: [ {
                            locationId,
                            mode,
                            roleNames: [ ...new Set( roles.map( ( role ) => role.name ) ) ], // remove duplicates
                        } ],
                    },
                } );
                await this.$store.dispatch( 'snackbar', {
                    message: 'Permissions Updated!',
                    type: 'success',
                    duration: 5000,
                } );
            } catch ( error ) {
                await this.$store.dispatch( 'snackbar', {
                    message: 'Error Updating Permissions',
                    type: 'error',
                    duration: 5000,
                } );
            }
            this.clearModifyModal();
        },

        addRoleHandler( { location, type } ) {
            this.setModifyModal( {
                show: true,
                type,
                location,
            } );
        },
        async removeRoleHandler( { location, roles } ) {
            await this.modifyRolesForLocation( {
                locationId: location.id,
                roles,
                mode: 'remove',
            } );
            await this.refreshPermissionsList();
        },

        async assignRolesHandler( { locationId, roles } ) {
            await this.modifyRolesForLocation( {
                locationId,
                roles,
                mode: 'add',
            } );
            await this.refreshPermissionsList();
        },
    },
};
</script>

<style scoped lang="scss">
.permission-modification {
    ::v-deep.v-expansion-panel {
        .v-expansion-panel-header {
            padding: 0;
        }
        .v-expansion-panel-content {
            .v-expansion-panel-content__wrap {
                padding: 0;
            }
        }
    }
    &__input-group {
        display: flex;
        flex-flow: column nowrap;

        &__buttons {
            display: flex;
            flex-flow: row nowrap;
            margin-top: 1rem;
            justify-content: space-between;
            >* {
                width: 48%;
            }
        }

        .button {
            &__view-permissions {
                margin-top: 1rem;
                width: 100%
            }
        }
    }
}
@include mixins-above(tablet) {
    .permission-modification {
        &__input-group {
            flex-flow: row nowrap;

            &--main {
                display: flex;
                flex-flow: column nowrap;
                width: 85%;
            }

            &__buttons {
                display: flex;
                margin-top: 1rem;
                justify-content: unset;
                >* {
                    width: auto;
                    margin: 0;
                }
            }
            .button {
                &__current-user {
                    margin-left: 1rem;
                }
                &__view-permissions {
                    margin-top: 0;
                    margin-right: 0;
                    margin-left: auto;
                    width: auto;
                }
            }
        }
    }
}
</style>
